/* eslint-disable no-useless-return */
/* eslint-disable sonarjs/no-redundant-jump */
import type { CanvasContext, DrawingContext } from '@studio/design-system/canvas';
import type { Coordinate } from '@studio/js-utils/geometry';
import type { Device } from '@studio/models/device';
import { useLatestFn, useShallowEffect, χref } from '@studio/react-utils/hooks';
import { noop } from 'lodash-es';
import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { getLastKnownPosition } from '../cursor/cursor.jsx';
import { EnterKeys, EscapeKeys, InitialScale } from './constants.js';
import {
  setFastFrameInitialScale,
  setFastFrameOnExitTool,
  setFastFrameSelectStage,
  setToolInitialPosition,
  useFastFrameInitialScale,
  useFastFrameOnExitTool,
  useFastFrameSelectStage,
  useToolInitialPosition
} from './hooks.js';
import type { Wiring } from './types.js';
import { FastFrameSelectStage } from './types.js';
import { drawCursor } from './utils.js';
import {
  useAnalytics,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  MOVEMENT_TOOLS_PROPERTIES
} from '#~/analytics/index.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { Command } from '#~/command/index.js';
import { useGridLinesPref, useToolPref, useToolVariants } from '#~/control/hooks.js';
import { Tool } from '#~/control/index.js';
import { moveTo } from '#~/control/movement/commands/index.js';

/**
 * Fast frame wiring.
 *
 * @param deviceId - Device ID.
 * @param canvasRef - Cursor canvas reference.
 * @returns Fast frame wiring.
 */
export const useWiring = (
  deviceId: Device['id'],
  canvasRef: RefObject<CanvasContext | null>
): Wiring => {
  const [gridLines] = useGridLinesPref();
  const [isDragging, setIsDragging] = useState(false);
  const { track } = useAnalytics();

  const [fastFrameSelectionPosition, setFastFrameSelectionPosition] = useState<Coordinate>({
    x: 0,
    y: 0
  });

  const [fastFrameSelectionSize, setFastFrameSelectionSize] = useState<{
    canvasHeight: number;
    canvasWidth: number;
    height: number;
    width: number;
  }>({
    canvasHeight: 0,
    canvasWidth: 0,
    height: 0,
    width: 0
  });

  const [fastFrameDragStartCursorPoint, setFastFrameDragStartCursorPoint] = useState<Coordinate>({
    x: 0,
    y: 0
  });

  const [
    fastFrameDragStartFastFrameSelectionPosition,
    setFastFrameDragStartFastFrameSelectionPosition
  ] = useState<Coordinate>({ x: 0, y: 0 });

  const stateScale = useFastFrameInitialScale();
  const [scale, setScale] = useState(stateScale === -1 ? InitialScale : stateScale);

  const stateOnExitTool = useFastFrameOnExitTool();
  const onExitTool = useRef(stateOnExitTool).current;

  const [_, setTool] = useToolPref();

  const statePosition = useToolInitialPosition();
  const fastFrameSelectStage = useFastFrameSelectStage();

  const initialPosition = useRef(statePosition).current ?? getLastKnownPosition();

  const handleExit = useLatestFn((pointerLeave: boolean = false): void => {
    if (onExitTool === undefined) {
      return;
    }

    if (onExitTool === Tool.Center && !pointerLeave) {
      setToolInitialPosition(fastFrameSelectionPosition);
    }

    setTool(onExitTool);
  });

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  const [variants] = useToolVariants();
  const useCine = variants[Tool.FastFrame] === Command.TOOL_CINE_FRAME;

  const handleSelectFastFrame = useLatestFn((): void => {
    let { x, y } = fastFrameSelectionPosition;
    const { canvasHeight, canvasWidth } = fastFrameSelectionSize;
    const { height, width } = fastFrameSelectionSize;

    const centerXMin = width / 2;
    const centerYMin = height / 2;

    const centerXMax = canvasWidth - centerXMin;
    const centerYMax = canvasHeight - centerYMin;

    x = Math.max(centerXMin, Math.min(centerXMax, x));
    y = Math.max(centerYMin, Math.min(centerYMax, y));

    x /= canvasWidth;
    y /= canvasHeight;

    if (isProtected) {
      triggerControlAlert?.(() => {
        moveTo(
          deviceId,
          {
            center: { x, y },
            scale
          },
          useCine,
          { toolUsed: useCine ? 'cine_frame' : 'fast_frame' }
        ).catch(noop);
      });

      return;
    }

    moveTo(
      deviceId,
      {
        center: { x, y },
        scale
      },
      useCine,
      { toolUsed: useCine ? 'cine_frame' : 'fast_frame' }
    )
      .then(() => {
        // Track the fast frame shot event
        const eventName = useCine
          ? EVENT_NAMES.CONFIRM_CINE_FRAME_SHOT
          : EVENT_NAMES.CONFIRM_FAST_FRAME_SHOT;

        track(eventName, EVENT_CATEGORIES.MOVEMENT_TOOLS, {
          [MOVEMENT_TOOLS_PROPERTIES.SHOT_TYPE]: useCine ? 'cine' : 'standard',
          x: x.toFixed(4),
          y: y.toFixed(4),
          scale: scale.toFixed(4)
        });
      })
      .catch(noop);
  });

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (EscapeKeys.includes(event.key)) {
        if (fastFrameSelectStage !== FastFrameSelectStage.INITIAL) {
          setFastFrameSelectStage(FastFrameSelectStage.INITIAL);
        }

        handleExit();
      }

      if (EnterKeys.includes(event.key) && fastFrameSelectStage === FastFrameSelectStage.CONFIRM) {
        handleSelectFastFrame();
        setFastFrameSelectStage(FastFrameSelectStage.INITIAL);
        handleExit();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    if (scale !== -1) {
      setFastFrameInitialScale(-1);
    }

    if (onExitTool !== undefined) {
      setFastFrameOnExitTool(undefined);
    }

    if (initialPosition !== undefined) {
      setToolInitialPosition(undefined);
    }

    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSelectFastFrame, handleExit, scale, onExitTool, initialPosition, fastFrameSelectStage]);

  const handleWheelEvent = useLatestFn((event: React.WheelEvent): void => {
    setScale((previousScale) =>
      Math.min(Math.max(Math.sign(event.deltaY) * 0.005 + previousScale, 0), 1)
    );
  });

  const clickHitFastFrameSelectionTest = useLatestFn((event: React.MouseEvent): boolean => {
    const maxX = fastFrameSelectionPosition.x + fastFrameSelectionSize.width / 2;
    const maxY = fastFrameSelectionPosition.y + fastFrameSelectionSize.height / 2;
    const minX = fastFrameSelectionPosition.x - fastFrameSelectionSize.width / 2;
    const minY = fastFrameSelectionPosition.y - fastFrameSelectionSize.height / 2;

    const targetRect = χref(canvasRef)?.canvas?.getBoundingClientRect();

    const adjustedCursorPoint = {
      x: event.clientX - (targetRect?.left ?? 0),
      y: event.clientY - (targetRect?.top ?? 0)
    };

    return !(
      adjustedCursorPoint.x > maxX ||
      adjustedCursorPoint.x < minX ||
      adjustedCursorPoint.y > maxY ||
      adjustedCursorPoint.y < minY
    );
  });

  const handleClickEvent = useLatestFn((event: React.MouseEvent): void => {
    if (fastFrameSelectStage === FastFrameSelectStage.INITIAL) {
      setFastFrameSelectStage(FastFrameSelectStage.CONFIRM);

      return;
    }

    const hit = clickHitFastFrameSelectionTest(event);

    if (hit) {
      handleSelectFastFrame();
    }

    setFastFrameSelectStage(FastFrameSelectStage.INITIAL);
    handleExit();
  });

  const handleDragStart = useLatestFn((event: React.MouseEvent): void => {
    if (fastFrameSelectStage === FastFrameSelectStage.INITIAL) {
      return;
    }

    setFastFrameDragStartCursorPoint({ x: event.clientX, y: event.clientY });

    const hit = clickHitFastFrameSelectionTest(event);

    if (!hit) {
      return;
    }

    setIsDragging(true);
    setFastFrameDragStartFastFrameSelectionPosition(fastFrameSelectionPosition);
  });

  const handleDrag = useLatestFn((event: React.MouseEvent): void => {
    if (fastFrameSelectStage === FastFrameSelectStage.INITIAL || !isDragging) {
      return;
    }

    const currentPosition = { x: event.clientX, y: event.clientY };

    const delta = {
      x: currentPosition.x - fastFrameDragStartCursorPoint.x,
      y: currentPosition.y - fastFrameDragStartCursorPoint.y
    };

    setFastFrameSelectionPosition({
      x: fastFrameDragStartFastFrameSelectionPosition.x + delta.x,
      y: fastFrameDragStartFastFrameSelectionPosition.y + delta.y
    });

    return;
  });

  const handleDragEnd = useLatestFn((event: React.MouseEvent): void => {
    setIsDragging(false);

    if (fastFrameSelectStage === FastFrameSelectStage.INITIAL) {
      handleClickEvent(event);

      return;
    }

    const dragDelta = {
      x: event.clientX - fastFrameDragStartCursorPoint.x,
      y: event.clientY - fastFrameDragStartCursorPoint.y
    };

    const dragDistance = Math.sqrt(dragDelta.x ** 2 + dragDelta.y ** 2);

    if (dragDistance < 10) {
      handleClickEvent(event);
    }
  });

  const handlePointerLeave = useLatestFn((): void => {
    handleExit(true);

    if (fastFrameSelectStage === FastFrameSelectStage.INITIAL) {
      return;
    }

    setFastFrameSelectStage(FastFrameSelectStage.INITIAL);
  });

  const handleDrawCursor = useLatestFn((context: DrawingContext, position?: Coordinate): void => {
    const selectPosition =
      fastFrameSelectStage === FastFrameSelectStage.INITIAL && position
        ? position
        : fastFrameSelectionPosition;

    setFastFrameSelectionPosition(selectPosition);

    const canvasWidth = context.surface.canvas.width / devicePixelRatio;
    const canvasHeight = context.surface.canvas.height / devicePixelRatio;

    // Calculate the width and height of the bounding box
    const width = canvasWidth * scale;
    const height = canvasHeight * scale;

    setFastFrameSelectionSize({ canvasHeight, canvasWidth, height, width });

    drawCursor(
      {
        ...context,
        fastFrameSelectPosition: selectPosition,
        gridLines,
        scale,
        stage: fastFrameSelectStage
      },
      position
    );
  });

  const handleRedraw = useLatestFn(() => {
    canvasRef.current?.draw();
  });

  // Redraw cursor if necessary.
  useShallowEffect(() => {
    handleRedraw();
  }, [fastFrameSelectStage, fastFrameSelectionPosition, scale]);

  return {
    handleDrag,
    handleDragEnd,
    handleDragStart,
    handleDrawCursor,
    handlePointerLeave,
    handleWheelEvent,
    initialPosition,
    useCine
  };
};
